import { useState, useEffect, useCallback } from 'react';

/**
 * Hook for detecting online/offline status
 * @returns {Object} - { isOnline, wasOffline, lastOnline }
 */
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const [lastOnline, setLastOnline] = useState(new Date());

  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Back online');
      setIsOnline(true);
      setLastOnline(new Date());

      if (wasOffline) {
        // Trigger data sync when coming back online
        window.dispatchEvent(new CustomEvent('app:online', {
          detail: { timestamp: new Date() }
        }));
      }
      setWasOffline(false);
    };

    const handleOffline = () => {
      console.log('📴 Gone offline');
      setIsOnline(false);
      setWasOffline(true);

      // Save all pending data when going offline
      window.dispatchEvent(new CustomEvent('app:offline', {
        detail: { timestamp: new Date() }
      }));
    };

    // Add listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  // Check connection quality
  const checkConnection = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const response = await fetch('/favicon.ico', {
        signal: controller.signal,
        cache: 'no-store'
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      console.warn('Connection check failed:', error);
      return false;
    }
  }, []);

  return {
    isOnline,
    wasOffline,
    lastOnline,
    checkConnection
  };
};

export default useOnlineStatus;
